import React, { useState, useEffect, useRef } from 'react'
import sanitizeHtml from 'sanitize-html-react'

import Combobox      from '@shared/combobox'
import ComboboxMulti from '@shared/combobox_multi'
import { getRequest, pathname } from '@helpers/javascript/javascript'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const TYPES = {
  client:    'client',
  site:      'site',
  material:  'material',
  materials: 'materials'
}

const DEFAULTS = {
  disabled: {
    client:       false,
    site:         false,
    material:     false,
    materials:    false,
    meters:       false,
    contract:     false,
    mbc_contract: false
  },
  required:   {
    client:       true,
    site:         true,
    material:     true,
    materials:    true,
    meters:       true,
    contract:     false,
    mbc_contract: false
  },
  showLabels: {
    client:       true,
    site:         true,
    material:     true,
    materials:    true,
    meters:       true,
    contract:     true,
    mbc_contract: true
  },
  inputNames: {
    client:       "client_id",
    site:         "site_id",
    material:     "material_id",
    contact:      "contact_id",
    materials:    "material_ids",
    meters:       "meter_ids",
    address:      "address",
    contract:     "contract_id",
    mbc_contract: "mbc_contract_id"
  },
  labels: {
    client:       I18n.t("client.client"),
    site:         I18n.t("sites.site"),
    address:      I18n.t("workorders.address"),
    contact:      I18n.t("contacts.contact"),
    material:     I18n.t("materials.material"),
    materials:    I18n.t("materials.materials"),
    meters:       I18n.t("meters.meters"),
    contract:     I18n.t("maintenance.contract"),
    mbc_contract: I18n.t("mbc_contracts.label"),
  },
  placeholder: {
    client:          I18n.t("workorders.select_client"),
    site:            I18n.t("workorders.select_site"),
    contact:         I18n.t("workorders.select_contact"),
    no_contact:      I18n.t("client.no_contact"),
    material:        I18n.t("workorders.select_material"),
    noMaterial:      I18n.t("maintenance.no_material"),
    meters:          I18n.t("meters.select_meter"),
    noMeter:         I18n.t("meters.no_meters"),
    contract:        I18n.t("workorders.select_contract"),
    no_contract:     I18n.t("client.no_contracts"),
    mbc_contract:    I18n.t("workorders.select_contract"),
    no_mbc_contract: I18n.t("client.no_contracts")
  }
}

const toDataSet = dataObject => {
  const data  = {}
  Object.keys(dataObject).forEach(key =>{
    const dataKey = "data-" + key.toString()
    data[dataKey] = dataObject[key]
  })
  return data
}

const filterContacts = (client, site) => {
  if (!client.id) return []

  const fromClient = client.contacts.filter(contact =>
    !site.id || contact.site_ids.length === 0 || contact.site_ids.includes(site.id)
  )

  if (!site.id) return fromClient

  const fromClientIds = new Set(fromClient.map(c => c.id))
  const fromSite = site.contacts.filter(c => !fromClientIds.has(c.id))

  return [...fromClient, ...fromSite]
}

const AssetSelect = props => {
  const clientRef      = useRef(null)
  const siteRef        = useRef(null)
  const contactRef     = useRef(null)
  const materialRef    = useRef(null)
  const contractRef    = useRef(null)
  const mbcContractRef = useRef(null)
  const didMountRef    = useRef(null)
  const formRef        = useRef(null)

  const {
    name,
    type     = TYPES.client,
    data     = {},
    options  = {},
    datasets = {},
    onChange = false
  } = props

  const {
    displayClient       = true,
    displayAddress      = false,
    displayContact      = false,
    displayMaterials    = false,
    displayMeters       = false,
    displayHistory      = false,
    displayUnpaid       = false,
    displayObservation  = false,
    displayLink         = false,
    includeContracts    = false,
    includeMbcContracts = false,
    printData           = false,
    skipClientPrefill   = false
  } = options

  const disabled    = {...DEFAULTS.disabled,    ...options.disabled    || {} }
  const showLabels  = {...DEFAULTS.showLabels,  ...options.showLabels  || {} }
  const inputNames  = {...DEFAULTS.inputNames,  ...options.inputNames  || {} }
  const required    = {...DEFAULTS.required,    ...options.required    || {} }
  const labels      = {...DEFAULTS.labels,      ...options.labels      || {} }
  const placeholder = {...DEFAULTS.placeholder, ...options.placeholder || {} }

  const dataClient      = toDataSet(datasets.client || {})
  const dataSite        = toDataSet(datasets.site || {})
  const dataMaterial    = toDataSet(datasets.material || {})
  const dataContact     = toDataSet(datasets.contact || {})
  const dataContract    = toDataSet(datasets.contract || {})
  const dataMbcContract = toDataSet(datasets.mbcContract || {})

  const [state, setState] = useState({
    client:       {},
    site:         {},
    material:     {},
    contact:      {},
    contract:     {},
    mbc_contract: {},
    materials:    [],
    meters:       [],
    mounted:      false
  })

  const { client, site, material, contact, contract, mbc_contract, materials, meters, mounted } = state

  const clientOptions      = data.clients || client.id ? [{ id: client.id, name: client.name }] : []
  const nonEmpty = a => Array.isArray(a) && a.length > 0
  const clientSites        = nonEmpty(data.sites) ? data.sites : nonEmpty(client.sites) ? client.sites : []
  // A site moved to another client keeps its past records on the old client: keep it selectable.
  const siteOptions        = site.id && !clientSites.some(option => option.id == site.id)
                               ? [...clientSites, { id: site.id, name: site.name }]
                               : clientSites
  const materialOptions    = site.materials || []
  const meterOptions       = (site.meters || []).filter(meter => !material.id || meter.material_id == material.id)
  const contactOptions     = filterContacts(client, site)
  const contractOptions    = client.contracts || []
  const mbcContractOptions = client.mbc_contracts || []

  const clientFeed = data.clients ? null : "/clients/autocomplete.json"
  const siteFeed   = client.id    ? null : "/sites/autocomplete.json"

  useEffect(() => {
    if (data.client) {
      selectClient(data.client, data.site, data.material, data.materials, data.contract, data.mbcContract, data.meters)
    } else if (data.site) {
      selectSite(data.site, data.material, data.materials, data.meters)
    } else {
      setState({...state, mounted: true })
    }
  }, [])

  useEffect(() => {
    if(didMountRef.current && client && clientRef.current && datasets.client) {
      clientRef.current.dispatchEvent(new Event("change"))
    }
  }, [client])

  useEffect(() => {
    if (didMountRef.current && site && siteRef.current && datasets.site) {
      siteRef.current.dispatchEvent(new Event("change"))
    }
  }, [site])

  useEffect(() => {
    if(didMountRef.current && material && materialRef.current && datasets.material) {
      materialRef.current.dispatchEvent(new Event("change"))
    }
  }, [material])

  useEffect(() => {
    if(didMountRef.current && contact && contactRef.current && datasets.contact) {
      contactRef.current.dispatchEvent(new Event("change"))
    }
  }, [contact])

  useEffect(() => {
    if(didMountRef.current && contract && contractRef.current && datasets.contract) {
      contractRef.current.dispatchEvent(new Event("change"))
    }
  }, [contract])

  useEffect(() => {
    if(didMountRef.current && mbc_contract && mbcContractRef.current && datasets.mbcContract) {
      mbcContractRef.current.dispatchEvent(new Event("change"))
    }
  }, [mbc_contract])

  useEffect(() => {
    if(mounted) { didMountRef.current = true }
  }, [mounted])

  const disableFormButtons = disabled => {
    const form = formRef.current?.closest("form")
    if (form) form.querySelectorAll('[type=submit]').forEach(submit => submit.disabled = !!disabled)
  }

  const updateState = newState => {
    if (onChange && didMountRef.current) onChange({ ...state, ...newState})
    setState(prevState => ({...prevState, ...newState}))
  }

  const fetchClientUrl = (clientId) => {
    const path   = `/clients/${clientId}/build`
    const format = 'json'
    const params = {}
    if (!displayContact)                           { params.skip_contacts  = true }
    if (includeContracts)                           { params.include_contracts = true }
    if (includeMbcContracts)                       { params.include_mbc_contracts = true }
    if (type == TYPES.client)                      { params.skip_sites = true }
    return pathname({ path, format, params })
  }

  const selectClient = async (clientId, siteId, materialId, materialIds, contractId, mbcContractId, meterIds) => {
    if (clientId) {
      disableFormButtons(true)
      const newClient    = await getRequest(fetchClientUrl(clientId))
      const siteUrl      = siteId  ? fetchSiteUrl(siteId, [materialId, ...(materialIds || [])]) : newClient.sites.length == 1 ? fetchSiteUrl(newClient.sites[0].id) : null
      const newSite      = siteUrl ? await getRequest(siteUrl) : {}
      const newMaterial  = siteUrl && materialId  ? newSite.materials.find(siteMaterial => siteMaterial.id == materialId) : {}
      const newMaterials = siteUrl && materialIds ? newSite.materials.filter(siteMaterial => materialIds.includes(siteMaterial.id)) : []
      const newMeters    = siteUrl && meterIds ? (newSite.meters || []).filter(siteMeter => meterIds.includes(siteMeter.id)) : []
      const newContract  = contractId    ? newClient.contracts?.find(c => c.id == contractId)       || {} : {}
      const newMbcContract = mbcContractId ? (newClient.mbc_contracts?.find(c => c.id == mbcContractId) || {}) : {}
      updateState({ client: newClient, site: newSite, material: newMaterial, materials: newMaterials, meters: newMeters, contract: newContract, mbc_contract: newMbcContract, mounted: true })
      disableFormButtons(false)
    } else {
      updateState({ client: {}, site: {}, contact: {}, material: {}, materials: [], meters: [], contract: {}, mbc_contract: {}, mounted: true })
    }
  }

  // Chosen materials stay offered even after they left the site.
  const fetchSiteUrl = (siteId, keptMaterialIds = []) => {
    const path   = `/sites/${siteId}/build`
    const format = 'json'
    const params = {}
    const kept   = keptMaterialIds.filter(Boolean)
    if (kept.length > 0) { params.kept_material_ids = kept.join(',') }
    if (!displayContact) { params.skip_contacts = true }
    if ([TYPES.client, TYPES.site].includes(type) && !displayMaterials) { params.skip_materials = true }
    if (displayMeters) { params.include_meters = true }
    return pathname({ path, format, params })
  }

  const selectSite = async (siteId, materialId, materialIds, meterIds) => {
    if (siteId) {
      disableFormButtons(true)
      let newClient = {}
      const newSite      = await getRequest(fetchSiteUrl(siteId, [materialId, ...(materialIds || [])]))
      if(!skipClientPrefill) {
        newClient = client.id ? client : await getRequest(fetchClientUrl(newSite.client_id))
      }
      const newMaterial  = materialId  ? newSite.materials.find(siteMaterial => siteMaterial.id == materialId) : {}
      const newMaterials = materialIds ? newSite.materials.filter(siteMaterial => materialIds.includes(siteMaterial.id)) : []
      const newMeters    = meterIds ? (newSite.meters || []).filter(siteMeter => meterIds.includes(siteMeter.id)) : []
      updateState({ client: newClient, site: newSite, material: newMaterial, materials: newMaterials, meters: newMeters, mounted: true })
      disableFormButtons(false)
    } else {
      updateState({ client: displayClient ? client : {}, site: {}, contact: {}, material: {}, materials: [], meters: [], mounted: true })
    }
  }

  const selectContact = newContact => {
    updateState({ contact: newContact || {} })
  }

  const selectContract = newContract => {
    updateState({ contract: newContract || {} })
  }

  const selectMbcContract = newContract => {
    // Explicit user pick or reset: stamp `explicit: true` so consumers can
    // tell this apart from the transient empty state during async loads.
    updateState({ mbc_contract: newContract ? newContract : { explicit: true } })
  }

  const selectMaterial = newMaterial => {
    updateState({ material: newMaterial || {} })
  }

  const selectMaterials = newMaterials => {
    updateState({ materials: newMaterials || [] })
  }

  const selectMeters = newMeters => {
    updateState({ meters: newMeters || [] })
  }

  return(
    <div ref={formRef} className="d-contents">
      { displayClient &&
        <div className="form-group">
          {showLabels.client && <label required={required.client}>{labels.client}</label>}

          <Combobox
            options         = {clientOptions}
            value           = {client.id}
            required        = {required.client}
            disabled        = {disabled.client}
            feed            = {clientFeed}
            placeholder     = {placeholder.client}
            onReset         = {selectClient}
            onSelectElement = {newClient => selectClient(newClient?.id)}
            resetIcon
          />

          { name &&
            <input
              name         = {`${name}[${inputNames.client}]`}
              defaultValue = {client.id}
              ref          = {clientRef}
              hidden
              {...dataClient}
            />
          }

          <div className="client-details">
            { displayObservation && client.observation &&
              <div className="no-margins observation mt-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(client.observation) }}
              >
              </div>
            }

            { displayUnpaid && client.unpaid_invoices && client.unpaid_invoices.count > 0 &&
              <div className="mt-2">
                <a href={client.unpaid_invoices.url}
                   className="pointer color-open"
                   target="_blank"
                >
                  { I18n.t(
                    "client.unpaid_invoices",
                    { number: client.unpaid_invoices.count }
                    )
                  }
                </a>
              </div>
            }

            { displayLink && client.modal_url &&
              <div
                data-toggle = "modal"
                data-target = "#template-modal"
                data-route  = {client.modal_url}
                data-action = "click->client--modal#show"
                className   = "pointer button-link mt-2"
              >
                { I18n.t("client.folder") }
              </div>
            }
          </div>
        </div>
      }

      { name && client.id && !displayClient &&
        <input
          name         = {`${name}[${inputNames.client}]`}
          defaultValue = {client.id}
          ref          = {clientRef}
          hidden
          {...dataClient}
        />
      }

      {/*Site*/}
      { [TYPES.site, TYPES.material, TYPES.materials].includes(type) &&
        <div className="form-group">
          {showLabels.site && <label required={required.site}>{labels.site}</label>}

          <Combobox
            options         = {siteOptions}
            value           = {site.id}
            required        = {required.site}
            disabled        = {disabled.site}
            placeholder     = {placeholder.site}
            feed            = {siteFeed}
            onReset         = {selectSite}
            onSelectElement = {newSite => selectSite(newSite?.id)}
            resetIcon
          />

          { name &&
            <input
              name         = {`${name}[${inputNames.site}]`}
              defaultValue = {site.id}
              ref          = {siteRef}
              required     = {required.site}
              hidden
              {...dataSite}
            />
          }

          <div className="client-details">
            { displayObservation && site.observation &&
              <div className="no-margins observation mt-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(site.observation) }}
              >
              </div>
            }

            { displayLink && site.modal_url &&
              <div
                data-toggle = "modal"
                data-target = "#template-modal"
                data-route  = {site.modal_url}
                data-action = "click->client--modal#show"
                className   = "pointer button-link mt-2 mr-1"
              >
                { I18n.t("sites.folder") }
              </div>
            }

            { displayHistory && site.history_url &&
              <a href      ={site.history_url}
                 target    ="_blank"
                 className = "pointer button-link mt-2"
              >
                {I18n.t('sites.site_history')}
                <FontAwesomeIcon icon='external-link-alt' className="ml-2"/>
              </a>
            }
          </div>
        </div>
      }

      { displayAddress &&
        <div className="form-group">
          {showLabels.site &&
            <label required={required.address}>{labels.address}</label>
          }
          <input
            className="form-control"
            defaultValue={site.address || ''}
            name={`${name}[${inputNames.address}]`}
            required
          />
        </div>
      }

      {/*Contact*/}
      { displayContact && (contactOptions.length > 0 || contact.id) &&
        <div className="form-group">
          <label required={required.contact}>{labels.contact}</label>

          <Combobox
            options         = {contactOptions}
            value           = {contact.id}
            required        = {required.contact}
            onSelectElement = {selectContact}
            onReset         = {selectContact}
            placeholder     = {
              client.id && contactOptions.length == 0
              ? placeholder.no_contact
              : placeholder.contact
            }
            resetIcon
          />

          { name &&
            <input
              name         = {`${name}[${inputNames.contact}]`}
              defaultValue = {contact.id}
              ref          = {contactRef}
              required     = {required.contact}
              hidden
              {...dataContact}
            />
          }
        </div>
      }

      {/*Contract*/}
      { includeContracts &&
        <div className="form-group">
          {showLabels.contract && <label required={required.contract}>{labels.contract}</label>}

          <Combobox
            options         = {contractOptions}
            value           = {contract.id}
            required        = {required.contract}
            disabled        = {disabled.contract}
            onSelectElement = {selectContract}
            onReset         = {selectContract}
            placeholder     = {
              client.id && contractOptions.length == 0
              ? placeholder.no_contract
              : placeholder.contract
            }
            resetIcon
          />

          { name &&
            <input
              name         = {`${name}[${inputNames.contract}]`}
              defaultValue = {contract.id}
              ref          = {contractRef}
              required     = {required.contract}
              hidden
              {...dataContract}
            />
          }
        </div>
      }

      {/*MBC Contract*/}
      { includeMbcContracts && (mbcContractOptions.length > 0 || mbc_contract.id) &&
        <div className="form-group">
          {showLabels.mbc_contract && <label required={required.mbc_contract}>{labels.mbc_contract}</label>}

          <Combobox
            options         = {mbcContractOptions}
            value           = {mbc_contract.id}
            required        = {required.mbc_contract}
            disabled        = {disabled.mbc_contract}
            onSelectElement = {selectMbcContract}
            onReset         = {selectMbcContract}
            placeholder     = {
              client.id && mbcContractOptions.length == 0
              ? placeholder.no_mbc_contract
              : placeholder.mbc_contract
            }
            resetIcon
          />

          { name &&
            <input
              name         = {`${name}[${inputNames.mbc_contract}]`}
              defaultValue = {mbc_contract.id}
              ref          = {mbcContractRef}
              required     = {required.mbc_contract}
              hidden
              {...dataMbcContract}
            />
          }
        </div>
      }

      {/*Material*/}
      { type == TYPES.material &&
        <div className="form-group">
          {showLabels.material &&
            <label required={required.material}>{labels.material}</label>
          }

          <Combobox
            options         = {materialOptions}
            value           = {material.id}
            required        = {required.material}
            disabled        = {disabled.material}
            onSelectElement = {selectMaterial}
            onReset         = {selectMaterial}
            placeholder     = {
              site.id && materialOptions.length == 0
              ? placeholder.noMaterial
              : placeholder.material
            }
            resetIcon
          />

          <div className="client-details">
            { displayLink && material.modal_url &&
              <div
                data-toggle = "modal"
                data-target = "#template-modal"
                data-route  = {material.modal_url}
                data-action = "click->client--modal#show"
                className   = "pointer button-link mt-2 mr-1"
              >
                { I18n.t("sites.folder") }
              </div>
            }
          </div>

          { name &&
            <input
              name         = {`${name}[${inputNames.material}]`}
              defaultValue = {material.id}
              ref          = {materialRef}
              required     = {required.material}
              hidden
              {...dataMaterial}
            />
          }
        </div>
      }

      {/*Materials*/}
      { type == TYPES.materials &&
        <div className="form-group">
          {showLabels.materials && <label required={required.materials}>{labels.materials}</label>}

           <ComboboxMulti
            options   = { materialOptions }
            onChange  = { selectMaterials }
            value     = { materials }
            required  = { required.materials }
            fieldName={name && `${name}[${inputNames.materials}]`}
            prompt   = {
              site.id && materialOptions.length == 0
                ? placeholder.noMaterial
                : placeholder.materials
            }
            allowAll
          />
        </div>
      }

      {/*Meters*/}
      { displayMeters &&
        <div className="form-group">
          {showLabels.meters && <label required={required.meters}>{labels.meters}</label>}

          <ComboboxMulti
            options   = { meterOptions }
            onChange  = { selectMeters }
            value     = { meters }
            required  = { required.meters }
            fieldName = { name && `${name}[${inputNames.meters}]` }
            prompt    = {
              site.id && meterOptions.length == 0
                ? placeholder.noMeter
                : placeholder.meters
            }
          />
        </div>
      }

      { printData &&
        <div id="asset-print" hidden>
          {JSON.stringify(state)}
        </div>
      }
    </div>
  )
}

export default AssetSelect
